import {
    AttachedFiles,
    type AttachedFilesRequest,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import moment from "moment";
import { type Moment } from "moment-recur-ts";

import { BTSelectItem } from "types/apiResponse/apiResponse";
import { DocumentInstanceType } from "types/enum";

import { getSelected } from "utilities/form/form";

import { ContactInfo } from "commonComponents/utilities/ContactInfoDisplay/ContactInfoDisplay.types";

import {
    type ILienWaiverTemplateVariables,
    parseLienWaiverStampedVariables,
} from "entity/lienWaiver/LienWaiverEditor/lienWaiverTemplateVariables";
import { type POPaymentStatus } from "entity/purchaseOrderPayment/common/PurchaseOrderPaymentStatus/PurchaseOrderPaymentStatus";

import { type default as LienWaiverJson } from "./LienWaiver.api.json";

type LienWaiverApiData = typeof LienWaiverJson;

export type LienWaiverFormActions =
    | "applyLienWaiver"
    | "deleteLienWaiver"
    | "sendLienWaiver"
    | "setLienWaiverStatus"
    | undefined;

export interface IHasLienWaiver {
    lienWaiverPurchaseOrderId?: number;
    lienWaiverBillId: number;
    jobId: number;
    status?: POPaymentStatus;
    isTemplate: boolean;
    canEdit: boolean;
    canAdd: boolean;
    lienWaiverDeclineMessage?: string;
    id: number;
    attachedLienWaiver?: LienWaiverEntity;
    lienWaiverForms?: BTSelectItem[];
}

export interface ILienWaiverSignatureFields {
    name: string;
    title: string;
}

export interface ILienWaiverFormValues {
    lienWaiverId: number;
    lienWaiverFormId?: number;
    lienWaiver?: LienWaiverEntity;
    lienWaiverAttachedFiles?: AttachedFilesRequest;
    status?: POPaymentStatus;
    sendLienWaiverWithPayment: boolean;
    lienWaiverType: LienWaiverType;
    lienWaiverStatus?: LienWaiverStatusTypes;
}

export enum LienWaiverType {
    Unknown = 0,
    Conditional = 1,
    Unconditional = 2,
}

export enum LienWaiverStatusTypes {
    Unreleased = 0,
    Released = 1,
    Approved = 2,
    Declined = 3,
    BuilderApproved = 4,
    Void = 5,
    BuilderDeclined = 6,
}

export enum LienWaiverStatusImages {
    Unreleased = "/images/Common/status/unrelease.png",
    Pending = "/images/Common/status/pending.png",
    SubApproved = "/images/Common/status/SubApprove.png",
    BuilderApproved = "/images/Common/status/BuilderApprove.png",
    Declined = "/images/Common/status/declinedItem.png",
    Void = "/images/POs/void.png",
}

export class LienWaiverConstants {
    public static readonly CreatedPaymentId = -2;
    public static readonly MadePaymentId = -3;
    public static readonly CreatedBillId = -102;
}

export class LienWaiverStatusInformation {
    constructor(data: any) {
        this.acceptedByName = data.acceptedByName || undefined;
        this.acceptedByTitle = data.acceptedByTitle || undefined;
        this.updatedDate = moment(data.updatedDate);
        this.signatureUrl = data.signatureUrl || undefined;
        this.status = data.status;
        this.statusText = data.statusText;
    }

    acceptedByName?: string;
    acceptedByTitle?: string;
    updatedDate: Moment;
    signatureUrl?: string;
    status: LienWaiverStatusTypes;
    statusText: string;
}

export class LienWaiverReleaseDetails {
    constructor(data: any) {
        this.savePdfDefault = data.savePdf && data.savePdf.value;
        this.savePdfPromptTitle = data.savePdf ? data.savePdf.title : "Save Pdf";
        this.titleForPdf = data.pdfTitle ? data.pdfTitle.value : "Pdf";
    }
    savePdfDefault: boolean;
    savePdfPromptTitle: string;
    titleForPdf: string;
}

export class LienWaiverEntity implements IHasAttachedFiles {
    private getSelectedLienWaiverForm(data: LienWaiverApiData) {
        if (this.id === 0 && this.lienWaiverForms) {
            return getSelected(this.lienWaiverForms)!.title;
        } else {
            return data.lienWaiverFormName ?? undefined;
        }
    }

    constructor(data: LienWaiverApiData) {
        this.id = data.id;
        // For new Lien Waivers, the API returns null / undefined as the id. We are safe
        // to change this to a 0, since the rest of our code expects 0 for new
        if (this.id === null || this.id === undefined) {
            this.id = 0;
        }
        this.jobId = data.jobId;
        this.builderId = data.builderId;
        this.billId = data.billId;
        this.status = data.status;
        this.lienWaiverType = data.lienWaiverType ?? LienWaiverType.Conditional;
        this.statusText = data.statusText ? data.statusText : "";
        this.lienWaiverText = data.lienWaiverText ? data.lienWaiverText : ""; // this field is only a placeholder for the purposes of testing the scaffolding
        this.subName = data.subName ? data.subName : undefined;
        this.jobName = data.jobName ? data.jobName : undefined;
        this.jobAddress = data.jobAddress
            ? new ContactInfo({ address: data.jobAddress })
            : undefined;
        this.lotInfo = data.lotInfo ? data.lotInfo : undefined;
        this.effectiveDateOfLienWaiver = data.effectiveDateOfLienWaiver
            ? moment(data.effectiveDateOfLienWaiver)
            : undefined;
        this.lienWaiverPaymentAmount = data.lienWaiverPaymentAmount
            ? data.lienWaiverPaymentAmount
            : undefined;
        this.attachedFiles = new AttachedFiles(data.attachedFiles);
        this.documentInstanceType = DocumentInstanceType.LienWaiver;
        this.lienWaiverForms = data.lienWaiverForms
            ? data.lienWaiverForms.map((lwf) => new BTSelectItem(lwf))
            : undefined;
        this.selectedLienWaiverForm = this.getSelectedLienWaiverForm(data);
        this.canEdit = data.canEdit;
        this.canDelete = data.canDelete;
        this.canApproveDecline = data.canApproveDecline;
        this.isOutboundEnabled = data.isOutboundEnabled;
        if (data.statusInformation) {
            this.statusInformation = new LienWaiverStatusInformation(data.statusInformation);
        }
        if (data.releaseDetails) {
            this.releaseDetails = new LienWaiverReleaseDetails(data.releaseDetails);
        }
        this.lienWaiverDialogMsg = data.lienWaiverDialogMsg ?? undefined;
        this.canViewPrice = data.canViewPrice === undefined || data.canViewPrice;
        this.name = "Lien Waiver";
        this.declineMessage = data.declineMessage ?? undefined;
        this.hasPendingPaymentServicePayment = data.hasPendingPaymentServicePayment;
        this.isPartOfOnlineMassPayment = data.isPartOfOnlineMassPayment;
        this.nutrientSigningShareToken = data.nutrientSigningShareToken ?? undefined;
        this.subId = data.subId ?? undefined;

        this.stampedLienWaiverVariables = parseLienWaiverStampedVariables(
            data.stampedLienWaiverVariables,
            this.canViewPrice,
            this.effectiveDateOfLienWaiver ? this.effectiveDateOfLienWaiver.toDate() : "",
        );
    }

    status: LienWaiverStatusTypes;
    lienWaiverType: LienWaiverType;
    statusText: string;
    lienWaiverText: string;
    subName?: string;
    jobName?: string;
    jobAddress?: ContactInfo;
    lotInfo?: string;
    effectiveDateOfLienWaiver?: moment.Moment;
    lienWaiverPaymentAmount?: number;
    documentInstanceType: DocumentInstanceType.LienWaiver;
    name: "Lien Waiver";
    attachedFiles: AttachedFiles;
    id: number;
    jobId: number;
    builderId: number;
    purchaseOrderId?: number;
    billId: number;
    lienWaiverForms?: BTSelectItem[];
    selectedLienWaiverForm?: string;
    canEdit: boolean;
    canDelete: boolean;
    canApproveDecline: boolean;
    statusInformation?: LienWaiverStatusInformation;
    releaseDetails?: LienWaiverReleaseDetails;
    isOutboundEnabled: boolean;
    lienWaiverDialogMsg?: string;
    canViewPrice: boolean;
    declineMessage?: string;
    hasPendingPaymentServicePayment?: boolean;
    isPartOfOnlineMassPayment?: boolean;
    /**
     * Share token scoped to signing this one waiver through Nutrient. Only present on external (sub) loads;
     * the page's own share token covers every waiver awaiting the sub and cannot identify which to sign.
     */
    nutrientSigningShareToken?: string;
    subId?: number;
    stampedLienWaiverVariables?: ILienWaiverTemplateVariables;
}

export class LienWaiverStatusEntity {
    constructor(data: any) {
        this.status = data.status;
        this.statusText = data.statusText;
        this.pdfLink = data.pdfLink;
        this.canRefreshPdf = data.canRefreshPdf;
    }
    status: LienWaiverStatusTypes;
    statusText: string;
    pdfLink: string;
    canRefreshPdf: boolean;
}

export interface ILienWaiverFormDefaultsRequest {
    lwFormId: number;
    poPaymentId?: number;
    poId?: number;
    billId?: number;
    jobId?: number;
}

export interface ILienWaiverCreateOrUpdateRequest {
    savePdf?: boolean;
    pdfTitle?: string;
    isMakingPayment?: boolean;
    poPaymentId?: number;
    sendLienWaiverWithPayment?: boolean;
    lienWaiverType?: LienWaiverType;
    lienWaiverForm?: number;
    lienWaiverStatus: LienWaiverStatusTypes;
    lienWaiverText?: string;
    attachedFiles: AttachedFilesRequest;
}

export class LienWaiverCreateOrUpdateResponse {
    constructor(data: any) {
        this.id = data.id;
    }

    id: number;
}

export class LienWaiverDeleteResponse {}

interface ILienWaiverApprovalResponseApi {
    didAutoAcceptPayment?: boolean;
    isAchEnabled: boolean;
    isAutoMailCheck: boolean;
}

export class LienWaiverApprovalResponse {
    constructor(data: ILienWaiverApprovalResponseApi) {
        this.didAutoAcceptPayment = data.didAutoAcceptPayment;
        this.isAchEnabled = data.isAchEnabled;
        this.isAutoMailCheck = data.isAutoMailCheck;
    }

    didAutoAcceptPayment?: boolean;
    isAchEnabled: boolean;
    isAutoMailCheck: boolean;
}

export interface ILienWaiverExternalRequest {
    encodedLienWaiverId: string;
    shareToken?: string;
}
